import { useEffect, useRef, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useLanguage } from '../useLanguage';

const GamesPage = () => {
  const { t } = useLanguage();
  const [emailRevealed, setEmailRevealed] = useState('');
  const emailLinkRef = useRef<HTMLAnchorElement>(null);

  const revealEmail = () => {
    const scrambled = 'moc.liamg@izerauj.nimajneb';
    const email = scrambled.split('').reverse().join('');
    setEmailRevealed(email);
  };

  useEffect(() => {
    if (emailRevealed) emailLinkRef.current?.focus();
  }, [emailRevealed]);

  return (
    <div className="min-h-screen bg-white dark:bg-dark-900">
      <Header activeSection="" />
      <main id="main-content" tabIndex={-1} className="pt-16">
        <section className="section-padding bg-white dark:bg-dark-900">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {t.games.title}
              </h1>
              <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                {t.games.subtitle}
              </p>
            </div>

            <div className="card p-8 mb-16">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {t.games.intro.heading}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {t.games.intro.body}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t.games.currentTitle}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">{t.games.currentSubtitle}</p>

            <div className="space-y-8 mb-16">
              {t.games.gamesList.map((game) => (
                <article key={game.title} className="card p-8">
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                      {game.title}
                    </h3>
                    <span className="inline-flex items-center rounded-full bg-primary-100 dark:bg-primary-900/30 px-3 py-1 text-sm font-medium text-primary-700 dark:text-primary-300">
                      {game.status}
                    </span>
                  </div>
                  <p className="text-lg text-secondary-700 dark:text-secondary-300 mb-4">
                    {game.tagline}
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                    {game.description}
                  </p>
                  <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                    {t.games.featuresHeading}
                  </h4>
                  <ul className="space-y-3">
                    {game.features.map((feature) => (
                      <li key={feature} className="flex items-start space-x-3">
                        <span className="text-primary-600 dark:text-primary-400 mt-0.5">✦</span>
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>

            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              {t.games.devNotesTitle}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">{t.games.devNotesSubtitle}</p>

            <div className="grid gap-6 md:grid-cols-2 mb-16">
              {t.games.devNotesList.map((note) => (
                <article key={note.title} className="card p-6">
                  <span className="inline-flex items-center rounded-full bg-secondary-100 dark:bg-secondary-900/30 px-3 py-1 text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-4">
                    {note.version}
                  </span>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {note.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{note.body}</p>
                </article>
              ))}
            </div>

            <div className="mb-16">
              <div className="bg-gradient-to-r from-gray-900 to-gray-800 dark:from-dark-800 dark:to-dark-900 text-white p-8 rounded-2xl shadow-xl">
                <h2 className="text-2xl font-bold mb-4">{t.games.futureTitle}</h2>
                <p className="text-lg opacity-90 max-w-3xl leading-relaxed">{t.games.futureBody}</p>
              </div>
            </div>

            <div className="card p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {t.games.supportTitle}
              </h2>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                {t.games.supportBody}
              </p>
              <div className="flex items-center space-x-4" aria-live="polite">
                <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-xl font-bold text-primary-600 dark:text-primary-400">@</span>
                </div>
                <div className="min-w-0">
                  <h3 className="font-medium text-gray-900 dark:text-white">{t.games.email}</h3>
                  {emailRevealed ? (
                    <a
                      ref={emailLinkRef}
                      href={`mailto:${emailRevealed}`}
                      className="min-h-11 inline-flex items-center break-all text-secondary-700 dark:text-secondary-300 hover:underline"
                    >
                      {emailRevealed}
                    </a>
                  ) : (
                    <button
                      onClick={revealEmail}
                      className="min-h-11 inline-flex items-center text-secondary-700 dark:text-secondary-300 hover:underline"
                    >
                      {t.games.clickToReveal}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GamesPage;